import { environment } from "@environments/environment";


const resolveBulkSalaryUpdatePath = (): string => {
  if (environment.bulkSalaryUpdatePath) {
    return environment.bulkSalaryUpdatePath;
  }
  const apiUrl = environment.API_URL ?? '';
  if (/localhost|127\.0\.0\.1/i.test(apiUrl)) {
    return '/bulk-salary-update';
  }
  return '/bulkSalaryUpdate';
};

const withClientIdQuery = (path: string, clientId?: string | null): string => {
  if (!clientId) {
    return path;
  }
  const encoded = encodeURIComponent(clientId);
  return path.includes('?') ? `${path}&clientId=${encoded}` : `${path}?clientId=${encoded}`;
};

export const API_URLS = {
  AUTH: {
    LOGIN: '/auth/login',
    SIGNUP: '/auth/sign-up',
    UPDATE_PASSWORD: '/auth/update-password',
    FORGOT_PASSWORD: '/auth/forgot-password',
    REFRESH_PASSWORD: '/auth/refresh-password',
    SAVE_EXPIRED_TOKEN: '/auth/save-token',
    EXPIRED_TOKEN: '/auth/expired-token',
    GET_TOKEN: (accessToken: string, projectId: string) => `/auth/expired-token/${accessToken}/${projectId}`,
    SIGNUP_WITH_TOKEN: (token: string) => `/auth/sign-up/${token}`,
    REFRESH_SESSION_TOKEN: '/auth/refresh-session-token',
    CLIENT_PORTAL_MAGIC_LINK_REDEEM: '/clientPortalMagicLink/redeem',
    CLIENT_PORTAL_SELECT_COMPANY: '/clientPortalMagicLink/select-company',
    CLIENT_PORTAL_PHONE_LOGIN_REQUEST: '/clientPortalMagicLink/request',
    CLIENT_PORTAL_SET_PASSWORD: '/clientPortalMagicLink/set-password',
    // 'projects' Cloud Function prefix required in prod (PublicProjectController lives inside
    // ProjectModule, which is deployed as the 'projects' Firebase Function — see index.ts).
    PUBLIC_PROJECT_APP_DOMAIN: (projectId: string) => `/projects/public/project-app-domain/${projectId}`,
  },
  USERS: {
    BASE: '/users',
    GET_BY_ID: (id: string) => `/users/${id}`,
    APPROVE: (id: string) => `/user/${id}/approve`,
  },
  USER_ROLE: {
    REPLACE: (userId: string) => `/userRole/${userId}/roles`,
  },
  PROJECT: {
    GENERATE_THEME: (projectId: string) => `/projects/themes/${projectId}`,
    SETUP_DOMAINS: (projectId: string) => `/projects/setup-domains/${projectId}`,
    SETUP_RECAPTCHA: (projectId: string) => `/projects/setup-recaptcha/${projectId}`,
  },
  DOMAIN_CONNECT: {
    DETECT: (projectId: string) => `/projects/domain-connect/${projectId}/detect`,
    START: (projectId: string) => `/projects/domain-connect/${projectId}/start`,
    STATUS: (projectId: string) => `/projects/domain-connect/${projectId}/status`,
    DISCONNECT: (projectId: string) => `/projects/domain-connect/${projectId}/disconnect`,
    CALLBACK: '/projects/domain-connect/callback',
    RETURN: '/projects/domain-connect/return',
    LINK: (projectId: string) => `/projects/domain-connect/${projectId}/link`,
    LINK_REVOKE: (projectId: string) => `/projects/domain-connect/${projectId}/link/revoke`,
    PUBLIC_INFO: (token: string) => `/projects/domain-connect/public/${token}/info`,
    PUBLIC_START: (token: string) => `/projects/domain-connect/public/${token}/start`,
    PUBLIC_STATUS: (token: string) => `/projects/domain-connect/public/${token}/status`,
    PUBLIC_COMPLETE: (token: string) => `/projects/domain-connect/public/${token}/complete`,
  },
  CLIENT: {
    BASE: '/client',
    GET_BY_ID: (id: string) => `/client/${id}`,
    CONNECT_BPS: (id: string) => `/client/${id}/connect-bps`,
    DISCONNECT_BPS: (id: string) => `/client/${id}/disconnect-bps`,
  },
  CLIENT_PROJECT: {
    BASE: '/client-project',
    GET_BY_ID: (id: string) => `/client-project/${id}`,
    GET_BY_CLIENT: (clientId: string) => `/client-project?clientId=${clientId}`,
  },
  TIMESHEET: {
    BASE: '/timesheet',
    GET_BY_ID: (id: string) => `/timesheet/${id}`,
    APPROVE: (id: string) => `/timesheet/${id}/approve`,
    REJECT: (id: string) => `/timesheet/${id}/reject`,
    PENDING: (id: string) => `/timesheet/${id}/pending`,
    SUMMARY: '/timesheet/summary',
  },
  PERMISSION: {
    SYNC: '/permission/sync',
  },
  EMPLOYEE: {
    BASE: '/employee',
    BY_CLIENT: (clientId: string) => `/employee?clientId=${encodeURIComponent(clientId)}`,
    BY_ID: (id: string, clientId?: string | null) => withClientIdQuery(`/employee/${id}`, clientId),
    ANNUL: (id: string, clientId?: string | null) => withClientIdQuery(`/employee/${id}/annul`, clientId),
    STATE: (id: string) => `/employee/${encodeURIComponent(id)}/state`,
    BPS_ALTA: (id: string, clientId?: string | null) => withClientIdQuery(`/employee/${id}/bps-alta`, clientId),
    BPS_ALTA_BATCH: '/employee/bps-alta/batch',
    SYNC: '/employee/sync',
    BULK_SALARY_UPDATE: resolveBulkSalaryUpdatePath(),
  },
  BPS_SELECTORS: {
    BASE: '/bpsSelectors',
    SYNC: '/bpsSelectors/sync',
    GENERAL_SETTINGS: (clientId: string) => `/bpsSelectors/general-settings?clientId=${clientId}`,
    LOCATIONS: (clientId: string) => `/bpsSelectors/locations?clientId=${clientId}`,
    ACUMULACION_LABORAL: '/bpsSelectors/acumulacion-laboral',
  },
  KEEPWARM: {
    HEALTHZ: '/keepwarm/healthz',
  },
  CONTRACTS: {
    SIGN: '/contracts/sign',
    SIGNING_CONTRACT: (projectId: string, contractId: string, token: string) =>
      `/contracts/signing-contract/${projectId}/${contractId}/${token}`,
    SEND_SIGNING_EMAILS: '/contracts/send-signing-emails',
    SEND_SIGNED_PDF: '/contracts/send-signed-pdf',
    COMPLETE_SIGNER: '/contracts/complete-signer',
  },
  STORAGE: {
    TRACK_STORAGE: (projectId: string) => `/usage/${projectId}/track-storage`,
  },
  AUTOMATED_FORMS: {
    HISTORY: '/automatedForms/history',
    GENERATE_F3_MULTI: '/automatedForms/generate-f3-multi',
    F3_FORMS: '/automatedForms/f3-forms',
    GENERATE_F3_SECTION: '/automatedForms/generate-f3-section',
    SET_MAIN_VERSION: '/automatedForms/f3-set-main-version',
    SAVE_F3_BASE: '/automatedForms/save-f3-base',
  },
  HELP_ASSISTANT: {
    TAXONOMY: '/helpAssistant/taxonomy',
    ASK: '/helpAssistant/ask',
  },
  CLIENT_DOCUMENT: {
    UPLOAD: '/clientDocument/upload',
    REPROCESS: (id: string) => `/clientDocument/${id}/reprocess`,
    GET_BY_ID: (id: string) => `/clientDocument/${id}`,
    REVIEW: (id: string) => `/clientDocument/${id}/review`,
    VIEW_URL: (id: string) => `/clientDocument/${id}/view-url`,
    MATERIALIZATION_PREVIEW: (id: string) => `/clientDocumentMaterialization/${id}/materialization-preview`,
    MATERIALIZE: (id: string) => `/clientDocumentMaterialization/${id}/materialize`,
    SEND_PREVIEW: (id: string) => `/clientDocumentMessaging/${id}/send-preview`,
    SEND: (id: string) => `/clientDocumentMessaging/${id}/send`,
    SEND_BULK_EMAIL: '/clientDocumentMessaging/send-bulk-email',
    SEND_BULK_WHATSAPP: '/clientDocumentMessaging/send-bulk-whatsapp',
    PUBLIC_DOCUMENT: '/clientDocumentPublic/document',
    // Client-portal read-only listing/download — mirrors bill.controller.ts's client-portal
    // routes: clientId is always resolved server-side from the authenticated user, never sent
    // by the caller.
    CLIENT_PORTAL_DOCUMENTS: '/clientDocument/client-portal/documents',
    CLIENT_PORTAL_DOCUMENT_VIEW_URL: (id: string) => `/clientDocument/client-portal/documents/${id}/view-url`,
  },
  ACCOUNTS_PAYABLE: {
    // 'accountsPayable' Cloud Function; `/suppliers` and `/bills-to-pay` are the controllers' static segments.
    SUPPLIERS: '/accountsPayable/suppliers',
    SUPPLIER: (id: string) => `/accountsPayable/suppliers/${id}`,
    BILLS: '/accountsPayable/bills-to-pay',
    BILL: (id: string) => `/accountsPayable/bills-to-pay/${id}`,
    MARK_PAID: (id: string) => `/accountsPayable/bills-to-pay/${id}/mark-paid`,
    CANCEL: (id: string) => `/accountsPayable/bills-to-pay/${id}/cancel`,
    REOPEN: (id: string) => `/accountsPayable/bills-to-pay/${id}/reopen`,
    RECURRENCE: (id: string) => `/accountsPayable/bills-to-pay/${id}/recurrence`,
    ATTACHMENT: (id: string) => `/accountsPayable/bills-to-pay/${id}/attachment`,
    ATTACHMENT_URL: (id: string) => `/accountsPayable/bills-to-pay/${id}/attachment-url`,
  },
  DOCUMENT_TYPE: {
    // `/document-types` static segment mirrors DocumentTypeController's prefix — see that controller's
    // comment: it disambiguates from ClientDocumentController's routes inside the shared Cloud Function.
    BASE: '/documentType/document-types',
    GET_BY_ID: (id: string) => `/documentType/document-types/${id}`,
  },
  DOCUMENT_MAPPING: {
    LIST: (clientId: string) => `/documentMapping/client/${clientId}`,
    UPSERT: (clientId: string, documentTypeId: string) => `/documentMapping/client/${clientId}/${documentTypeId}`,
  },
  CLIENT_DOCUMENT_API_CREDENTIAL: {
    BASE: '/clientDocumentApiCredential/admin/client-document-api-credentials',
    REVOKE: (id: string) => `/clientDocumentApiCredential/admin/client-document-api-credentials/${id}/revoke`,
  },
  CLIENT_DOCUMENT_INBOUND_EMAIL_TOKEN: {
    BASE: '/clientDocumentInboundEmailToken/admin/client-document-inbound-email-tokens',
    REVOKE: (id: string) => `/clientDocumentInboundEmailToken/admin/client-document-inbound-email-tokens/${id}/revoke`,
  },
  WEBSITE_IMPORT: {
    ANALYZE: '/websiteImport/analyze',
    APPLY: '/websiteImport/apply',
  },
  JOB_OFFER_WORKFLOW_INSTANCE: {
    CREATE_FROM_POSTULATION: '/jobOfferWorkflowInstance/create-from-postulation',
    ADVANCE_STEP: '/jobOfferWorkflowInstance/advance-step',
    RESUME: (instanceId: string) => `/jobOfferWorkflowInstance/${instanceId}/resume`,
    APPROVE: (instanceId: string) => `/jobOfferWorkflowInstance/${instanceId}/approve`,
    REJECT: (instanceId: string) => `/jobOfferWorkflowInstance/${instanceId}/reject`,
    LIST_DOCUMENTS: (instanceId: string, stepId: string) => `/jobOfferWorkflowInstance/${instanceId}/step/${stepId}/documents`,
    RECORD_DOCUMENT: (instanceId: string, stepId: string) => `/jobOfferWorkflowInstance/${instanceId}/step/${stepId}/documents`,
    APPROVE_DOCUMENT: (instanceId: string, docId: string) => `/jobOfferWorkflowInstance/${instanceId}/documents/${docId}/approve`,
    REJECT_DOCUMENT: (instanceId: string, docId: string) => `/jobOfferWorkflowInstance/${instanceId}/documents/${docId}/reject`,
    RESEND_CONTRACT: (instanceId: string) => `/jobOfferWorkflowInstance/${instanceId}/resend-contract`,
    RESEND_FORM_LINK: (instanceId: string) => `/jobOfferWorkflowInstance/${instanceId}/resend-form-link`,
  },
  PORTAL: {
    INSTANCE: (token: string) => `/portal/${token}`,
    ADVANCE_STEP: (token: string) => `/portal/${token}/advance-step`,
    RECORD_DOCUMENT: (token: string) => `/portal/${token}/record-document`,
    GUEST_REGISTER: (token: string) => `/portal/${token}/guest-register`,
    GUEST_LOGIN: (token: string) => `/portal/${token}/guest-login`,
  },
  ARTICLE: {
    CREATE: '/article',
    UPDATE: (articleId: string) => `/article/${articleId}`,
    DELETE: (articleId: string) => `/article/${articleId}`,
    SLUG_CHECK: '/article/slug-check',
    SEO_SUGGESTIONS: '/article/seo-suggestions',
    AI_DRAFT: '/article/ai-draft',
  },
  PAYMENT_DESTINATION: {
    BASE: '/paymentDestination',
    BY_ID: (id: string) => `/paymentDestination/${id}`,
    ACTIVE: (id: string) => `/paymentDestination/${id}/active`,
    ORDER: '/paymentDestination/order',
    SETTINGS: '/paymentDestination/settings',
    PARTIAL: '/paymentDestination/partial',
    PARTIAL_PREVIEW: '/paymentDestination/partial/preview',
  },
};
